import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Radio, CalendarClock, PlayCircle, CheckCircle2, Circle } from 'lucide-react';

/**
 * Catálogo de estados públicos de evento y su estilo visual.
 */
const STATUS_MAP = {
  scheduled: {
    label: 'Programado',
    icon: CalendarClock,
    className:
      'bg-[var(--ns-bg-2)] text-[var(--ns-text)] border border-[var(--ns-border)]',
  },
  published: {
    label: 'Próximo',
    icon: CalendarClock,
    className:
      'bg-[var(--ns-bg-2)] text-[var(--ns-text)] border border-[var(--ns-border)]',
  },
  live_scheduled: {
    label: 'Directo programado',
    icon: Radio,
    className:
      'bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] border border-[var(--ns-gold)]/40',
  },
  live: {
    label: 'EN DIRECTO',
    icon: Radio,
    className:
      'bg-[var(--ns-red)] text-white border border-[var(--ns-red)] animate-pulse',
  },
  finished: {
    label: 'Finalizado',
    icon: CheckCircle2,
    className:
      'bg-white text-[var(--ns-text-muted)] border border-[var(--ns-border)]',
  },
  ended: {
    label: 'Finalizado',
    icon: CheckCircle2,
    className:
      'bg-white text-[var(--ns-text-muted)] border border-[var(--ns-border)]',
  },
  replay_available: {
    label: 'Replay disponible',
    icon: PlayCircle,
    className:
      'bg-[var(--ns-green)]/12 text-[var(--ns-green-700)] border border-[var(--ns-green)]/35',
  },
};

const FALLBACK = {
  label: 'Evento',
  icon: Circle,
  className: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border border-[var(--ns-border)]',
};

export function EventStatusBadge({ status, size = 'sm', className = '', testId }) {
  const cfg = STATUS_MAP[status] || FALLBACK;
  const Icon = cfg.icon;
  const sizeCls = size === 'lg' ? 'text-[12px] sm:text-xs px-3 py-1.5' : 'text-[10px] sm:text-[11px] px-2 py-1';
  return (
    <Badge
      variant="secondary"
      className={`uppercase tracking-wider font-semibold inline-flex items-center gap-1 rounded-full ${cfg.className} ${sizeCls} ${className}`}
      data-testid={testId}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
}

const ACCESS_MAP = {
  free: { label: 'Gratis', cls: 'bg-[var(--ns-green)]/12 text-[var(--ns-green-700)] border-[var(--ns-green)]/35' },
  premium: { label: 'Premium', cls: 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] border-[var(--ns-gold)]/50' },
  subscription: { label: 'Suscripción', cls: 'bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] border-[var(--ns-gold)]/40' },
  pay_per_event: { label: 'Pago individual', cls: 'bg-white text-[var(--ns-text)] border-[var(--ns-border)]' },
  ppv: { label: 'Pago individual', cls: 'bg-white text-[var(--ns-text)] border-[var(--ns-border)]' },
};

export function EventAccessBadge({ accessType, className = '', testId }) {
  const cfg = ACCESS_MAP[accessType] || ACCESS_MAP.free;
  return (
    <Badge
      variant="secondary"
      className={`uppercase tracking-wider font-semibold text-[10px] sm:text-[11px] px-2 py-1 rounded-full border ${cfg.cls} ${className}`}
      data-testid={testId}
    >
      {cfg.label}
    </Badge>
  );
}

/** Utilidad: formatea event_date legible en es-ES. */
export function formatEventDate(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return '';
  }
}
